import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  CheckCheck, 
  Check, 
  RefreshCw, 
  AlertCircle, 
  Shield, 
  ExternalLink, 
  Clock
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export function UserNotificationsView() {
  const { session } = useAuth();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<'all' | 'unread'>('all');

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch('/api/user/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to load notifications');
      const data = await res.json();
      if (data.success) {
        setNotifications(data.data || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [session]);

  const markAsRead = async (id: string) => {
    try {
      const token = session?.access_token || localStorage.getItem('compass_token');
      await fetch(`/api/user/notifications/${id}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  const markAllAsRead = async () => {
    try {
      const token = session?.access_token || localStorage.getItem('compass_token');
      await fetch('/api/user/notifications/read-all', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {}
  };

  const displayedList = filterMode === 'unread' 
    ? notifications.filter(n => !n.is_read) 
    : notifications;

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-500" />
              <span>Notifications</span>
            </h1>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-rose-500 text-white">
                {unreadCount} new
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Real-time alerts triggered by state changes, review assignments, and governance gates across your Use Cases.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-card border border-border hover:bg-muted text-foreground transition-colors"
            >
              <CheckCheck className="h-3.5 w-3.5 text-emerald-500" />
              <span>Mark All as Read</span>
            </button>
          )}

          <button
            onClick={fetchNotifications}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-colors"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 text-xs flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-border pb-2 text-xs">
        <button
          onClick={() => setFilterMode('all')}
          className={cn(
            "px-3 py-1.5 rounded-lg font-medium transition-colors",
            filterMode === 'all' 
              ? "bg-blue-600 text-white shadow-xs" 
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          )}
        >
          All Notifications ({notifications.length})
        </button>
        <button
          onClick={() => setFilterMode('unread')}
          className={cn(
            "px-3 py-1.5 rounded-lg font-medium transition-colors",
            filterMode === 'unread' 
              ? "bg-blue-600 text-white shadow-xs" 
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          )}
        >
          Unread Only ({unreadCount})
        </button>
      </div>

      {/* NOTIFICATION CARDS */}
      <div className="space-y-3">
        {loading ? (
          <div className="p-16 flex flex-col items-center justify-center text-muted-foreground gap-3 rounded-xl border border-border bg-card">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            <span className="text-xs">Loading live notification events...</span>
          </div>
        ) : displayedList.length === 0 ? (
          <div className="p-16 text-center text-muted-foreground rounded-xl border border-border bg-card">
            <Bell className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-foreground">No notifications to display</p>
            <p className="text-xs mt-1">You are all caught up with your SOC detection notifications.</p>
          </div>
        ) : (
          displayedList.map((notif) => (
            <div
              key={notif.id}
              className={cn(
                "p-4 rounded-xl border transition-all flex items-start justify-between gap-4",
                !notif.is_read 
                  ? "bg-blue-500/5 border-blue-500/30 shadow-xs" 
                  : "bg-card border-border hover:bg-muted/20"
              )}
            >
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-bold text-xs mt-0.5",
                  !notif.is_read ? "bg-blue-600 text-white shadow-xs" : "bg-muted text-muted-foreground"
                )}>
                  <Shield className="h-4 w-4" />
                </div>

                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-xs text-foreground">{notif.title}</span>
                    <span className="font-mono text-[11px] font-semibold text-blue-500">
                      [{notif.use_case_number}]
                    </span>
                    {!notif.is_read && (
                      <span className="h-2 w-2 rounded-full bg-blue-600" />
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {notif.message}
                  </p>

                  <div className="flex items-center gap-4 text-[10px] text-muted-foreground pt-1">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{new Date(notif.created_at).toLocaleString()}</span>
                    </span>

                    {notif.ticket_id && (
                      <button
                        onClick={() => navigate(`/user/in-progress?focus=${notif.ticket_id}`)}
                        className="text-blue-500 hover:underline font-semibold flex items-center gap-1"
                      >
                        <span>View Related Use Case</span>
                        <ExternalLink className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {!notif.is_read && (
                <button
                  onClick={() => markAsRead(notif.id)}
                  className="shrink-0 p-1.5 rounded-lg border border-border hover:bg-muted text-muted-foreground hover:text-foreground text-[11px] flex items-center gap-1 font-medium transition-colors"
                  title="Mark as read"
                >
                  <Check className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Mark Read</span>
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
